import { api } from './api'
import type { RoomPipeline } from '../lib/pipeline'

export interface Room {
  id: string
  workspace_id: string
  name: string
  slug: string
  description?: string
  room_type: string
  yjs_doc_id?: string | null
  settings?: Record<string, any>
  is_active?: boolean
  created_by?: string
  created_at?: string
  updated_at?: string
  my_role?: string
  workspace_name?: string | null
}

export interface RoomInvitation {
  id: string
  room_id: string
  token: string
  invited_by: string | null
  role: string
  expires_at: string | null
  used_at: string | null
  used_by: string | null
  created_at: string
}

export interface InvitationPreview {
  room_name: string
  room_id: string
  inviter_name: string | null
  role: string
  expires_at: string | null
  is_valid: boolean
}

export async function getMyRooms(): Promise<Room[]> {
  const res = await api.get('/api/v1/rooms/my')
  return res.data
}

export async function createRoom(data: {
  workspace_id: string
  name: string
  description?: string
  room_type?: string
  slug?: string
  max_participants?: number
}) {
  const res = await api.post('/api/v1/rooms', data)
  return res.data
}

export async function createInvitation(roomId: string, role: string = 'editor', expiresInHours: number = 168): Promise<RoomInvitation> {
  const res = await api.post(`/api/v1/rooms/${roomId}/invitations`, {
    role,
    expires_in_hours: expiresInHours,
  })
  return res.data
}

export async function getInvitationPreview(token: string): Promise<InvitationPreview> {
  const res = await api.get(`/api/v1/invitations/${token}`)
  return res.data
}

export async function redeemInvitation(token: string) {
  const res = await api.post(`/api/v1/invitations/${token}/redeem`)
  return res.data
}

export async function joinRoom(roomId: string, inviteToken?: string) {
  const res = await api.post(`/api/v1/rooms/${roomId}/join`, null, {
    params: inviteToken ? { invite_token: inviteToken } : {},
  })
  return res.data
}

export async function leaveRoom(roomId: string) {
  await api.post(`/api/v1/rooms/${roomId}/leave`)
}

export async function getRoomMembers(roomId: string) {
  const res = await api.get(`/api/v1/rooms/${roomId}/members`)
  return res.data
}

export async function publishRoomPipeline(
  roomId: string,
  pipeline: RoomPipeline,
) {
  const res = await api.put(`/api/v1/rooms/${roomId}/pipeline`, pipeline)
  return res.data
}

export interface RoomStudioSnapshot {
  nodes: number
  edges: number
  pages: number
  last_agent?: string | null
  now: string
  updated_at: string
}

/** Resumo do Studio para o card do dashboard (`room.settings.studio`). */
export async function publishRoomStudio(roomId: string, snapshot: RoomStudioSnapshot) {
  const res = await api.put(`/api/v1/rooms/${roomId}/studio`, snapshot)
  return res.data
}

export interface StudioSuggestion {
  persona: 'vangogh' | 'jobs' | 'archie' | 'dochie'
  text: string
}

/** Primeiros pedidos sugeridos no chat do Studio, gerados a partir do nome e da descrição do projeto. */
export async function getStudioSuggestions(roomId: string): Promise<{ suggestions: StudioSuggestion[]; generic: boolean }> {
  const res = await api.get(`/api/v1/studio/${roomId}/suggestions`)
  return res.data
}

export async function getRoom(roomId: string): Promise<Room> {
  const res = await api.get(`/api/v1/rooms/${roomId}`)
  return res.data
}

export async function updateRoom(roomId: string, data: { name?: string; description?: string }) {
  const res = await api.put(`/api/v1/rooms/${roomId}`, data)
  return res.data
}

export async function deleteRoom(roomId: string) {
  await api.delete(`/api/v1/rooms/${roomId}`)
}

// ============================================================
// Workspaces
// ============================================================

export interface Workspace {
  id: string
  name: string
  description?: string
  owner_id: string
  settings?: Record<string, any>
  created_at: string
  updated_at: string
}

export async function getMyWorkspaces(): Promise<Workspace[]> {
  const res = await api.get('/api/v1/workspaces')
  return res.data.items || []
}

export async function createWorkspace(data: { name: string; description?: string }) {
  const res = await api.post('/api/v1/workspaces', data)
  return res.data
}

export interface ProjectApiKey {
  id: string
  name: string
  prefix: string
  created_at: string
  last_used_at?: string | null
  /** Permite as ferramentas MCP de escrita (diagrama do Studio, páginas, diagramas Mermaid). */
  can_write?: boolean
}

/** Chaves da conta: acessam todos os projetos do usuário pelo MCP. */
export async function listAccountApiKeys(): Promise<ProjectApiKey[]> {
  const res = await api.get('/api/v1/api-keys')
  return res.data
}

export async function createAccountApiKey(name: string, canWrite = false): Promise<ProjectApiKey & { key: string }> {
  const res = await api.post('/api/v1/api-keys', { name, can_write: canWrite })
  return res.data
}

export async function revokeAccountApiKey(keyId: string) {
  await api.delete(`/api/v1/api-keys/${keyId}`)
}

export async function listProjectApiKeys(roomId: string): Promise<ProjectApiKey[]> {
  const res = await api.get(`/api/v1/rooms/${roomId}/api-keys`)
  return res.data
}

export async function createProjectApiKey(roomId: string, name: string, canWrite = false): Promise<ProjectApiKey & { key: string }> {
  const res = await api.post(`/api/v1/rooms/${roomId}/api-keys`, { name, can_write: canWrite })
  return res.data
}

export async function revokeProjectApiKey(roomId: string, keyId: string) {
  await api.delete(`/api/v1/rooms/${roomId}/api-keys/${keyId}`)
}
